import formatDate from "@elsa-zku/lib/helpers/formatDate";
import { formatDateMonth } from "@elsa-zku/lib/helpers/formatDateMonth";
import parseDate from "@elsa-zku/lib/helpers/parseDate";
import { useDataContainer } from "@elsa-zku/lib/helpers/useDataContainer";
import * as React from 'react';
import { DisableNotification } from "src/api/data-contracts";
import { disableNotificationsControllerFindDisableNotifications } from "src/api/disableNotifications";
import {
  disableNotificationsApartmentTypes,
} from "src/disableNotifications/src/helpers/disableNotificationApartmentTypes";
import { DisableNotificationsFilter } from "src/disableNotifications/src/helpers/disableNotificationsFilter";
import style from './List.scss';
import Loader from "src/components/Loader";
import Icon from "src/components/Icon";

type DisableNotificationMonthInfo = {
  month: string,
  parsedMonth: Date,
  apartmentTypes: {
    id: number
    items: (DisableNotification & { numericNumber: number })[]
  }[]
}

type Props = {
  filter: DisableNotificationsFilter,
  availableYears: number[],
};

const sortByField = <O, >(fieldName: keyof O, desc?: boolean) => (e1: O, e2: O) => {
  const f1 = e1[fieldName];
  const f2 = e2[fieldName];
  return f1 === f2 ? 0 : (desc ? (f1 > f2 ? -1 : 1) : (f1 > f2 ? 1 : -1));
}

const List: React.FunctionComponent<Props> = (props) => {
  const { filter, availableYears } = props;
  const { building_id, search, year } = filter;

  const { data, loading, error } = useDataContainer(React.useCallback(() => {
    const finalYear = year || availableYears[0];
    if (!finalYear) return [] as DisableNotificationMonthInfo[];
    return disableNotificationsControllerFindDisableNotifications(building_id, {
      year:   finalYear,
      number: search || undefined,
    }).then((items): DisableNotificationMonthInfo[] => {
      const monthsDict: Record<string, {
        month: string,
        parsedMonth: Date,
        apartmentTypes: { id: number, items: (DisableNotification & { numericNumber: number })[] }[]
      }> = {};
      items.forEach((item) => {
        const { date, apartmentType } = item;
        let [year, month, day] = date.split('-');
        month = `${year}-${month}`;
        if (!monthsDict[month]) monthsDict[month] = {
          month:          month,
          parsedMonth:    parseDate(month),
          apartmentTypes: [],
        }
        if (!monthsDict[month].apartmentTypes[apartmentType]) monthsDict[month].apartmentTypes[apartmentType] = {
          id:    apartmentType,
          items: [],
        };
        monthsDict[month].apartmentTypes[apartmentType].items.push({
          ...item,
          numericNumber: +item.number.replace(/[^0-9.]/g, ""),
        });
      });

      const months = Object.values(monthsDict);
      months.sort(sortByField('month', true));
      months.forEach(month => {
        month.apartmentTypes.sort(sortByField('id'))
        month.apartmentTypes.forEach(apartmentType => {
          apartmentType.items.sort(sortByField('numericNumber'));
        })
      });
      return months;
    })
  }, [building_id, search, year]))

  if (error) return <div>{error}</div>;
  if (loading || !data) return <Loader />;
  if (!data.length) return <div className={style.placeholder}>Уведомления не найдены</div>
  return (
    <div className={style.container}>
      {data.map(month => (
        <div key={month.month} className={style.item}>
          <div className={style.title}>{formatDateMonth(month.month)}</div>
          {month.apartmentTypes.map(type => {
            const apartmentsType = disableNotificationsApartmentTypes.getById(type.id);
            return (
              <div key={type.id} className={style.apartmentType}>
                <div className={style.type}>
                  <Icon className={style.typeIcon} icon={apartmentsType.icon} />
                  {/*<div className={style.typeIcon} dangerouslySetInnerHTML={{ __html: apartmentsType.icon }} />*/}
                  {apartmentsType.plural} ({type.items.length})
                </div>
                {type.items.map(item => (
                  <a key={item.id} href={item.file} target="_blank" className={style.link}>
                    <span className={style.number}>{apartmentsType.short} № <b>{item.number}</b></span>
                    <span className={style.date}>{formatDate(item.date)}</span>
                    <Icon className={style.arrow} icon={require('src/disableNotifications/src/icons/chevron.svg')} />
                  </a>
                ))}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  );
};

export default React.memo(List);